import { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { MapPin, Mail, Phone, Clock, Facebook, Instagram } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { SectionHeading } from '@/components/ui/section-heading';

const Contact = () => {
  return (
    <PageLayout>
      <Helmet>
        <title>Contact OMAC-UK | Leading Taekwondo School in Nottingham</title>
        <meta name="description" content="Contact OMAC-UK, a premier Taekwondo school in Gedling, Nottingham. Led by Master Juliet Williams and Stuart Williams, offering competitive and community-focused British Taekwondo training." />
        <meta name="keywords" content="Taekwondo Nottingham, Martial Arts Nottingham, Taekwondo Classes, UK Taekwondo, Nottingham Taekwondo, Gedling Taekwondo, British Taekwondo, Taekwondo Classes, Master Juliet Williams, Self-Defence Training, World Taekwondo, MooGongRyu, Hapkido, Kids Taekwondo, Adult Martial Arts, OMAC-UK, OMAC UK" />
        <meta property="og:title" content="Contact OMAC-UK - Premier Taekwondo in Nottingham" />
        <meta property="og:description" content="Get in touch with OMAC-UK, Gedling's leading Taekwondo academy offering classes for all ages under Master Juliet Williams." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://omac-uk.co.uk/contact" />
        <meta property="og:image" content="/images/juliet/juliet_kick.jpeg" />
        <link rel="canonical" href="https://omac-uk.co.uk/contact" />
      </Helmet>

      <div className="pt-16 pb-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-300">Ready to start your Taekwondo journey? Our team is here to help!</p>
          </div>
        </div>
      </div>

      <div className="py-16">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Contact Us"
            gradient={true}
            className="mb-8"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="rounded-full bg-taekwondo-red/10 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-taekwondo-red" />
                </div>
                <h3 className="text-xl font-bold mb-3">Visit Us</h3>
                <p className="text-gray-600 mb-2">1st Floor</p>
                <p className="text-gray-600 mb-2">65 Gedling Road</p>
                <p className="text-gray-600 mb-2">Corner of Westdale Lane East</p>
                <p className="text-gray-600 mb-2">Gedling</p>
                <p className="text-gray-600 mb-2">Nottingham</p>
                <p className="text-gray-600 mb-2">NG4 3FG</p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="rounded-full bg-taekwondo-blue/10 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-taekwondo-blue" />
                </div>
                <h3 className="text-xl font-bold mb-3">Email Us</h3>
                <p className="text-gray-600 mb-2">General Inquiries:</p>
                <p className="text-taekwondo-blue hover:underline">
                  <a href="mailto:masterjuliet@omac-uk.co.uk">masterjuliet@omac-uk.co.uk</a>
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="rounded-full bg-taekwondo-red/10 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <Phone className="w-8 h-8 text-taekwondo-red" />
                </div>
                <h3 className="text-xl font-bold mb-3">Call Us</h3>
                <p className="text-gray-600 mb-2">Phone:</p>
                <p className="text-xl font-semibold text-taekwondo-red">
                  <a href="tel:01159874857" className="hover:underline" rel="noopener">0115 987 4857</a>
                </p>
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Office Hours</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Monday: 17:30 - 20:30</p>
                    <p>Tuesday: 16:30 - 20:00</p>
                    <p>Wednesday: 17:30 - 20:30</p>
                    <p>Thursday: 16:30 - 20:00</p>
                    <p>Saturday: 09:30 - 13:30</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 h-full">
              <CardContent className="p-8 text-center flex flex-col h-full">
                <div className="rounded-full bg-green-100 w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <a
                      href="https://wa.me/447495782541"
                      className="whatsapp-float"
                      target="_blank"
                      rel="noopener noreferrer"
                  >
                    <img
                        src="/images/icons/whatsapp.png"
                        alt="WhatsApp"
                        className="w-8 h-8"
                    />
                  </a>
                </div>
                <h3 className="text-xl font-bold mb-3">WhatsApp Us</h3>
                <p className="text-gray-600 mb-4">Direct Message:</p>
                
                <Button 
                  className="bg-green-500 hover:bg-green-600 text-white mb-4 w-full flex items-center justify-center gap-2"
                  asChild
                >
                  <a 
                    href="https://wa.me/447495782541" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/images/icons/whatsapp.png"
                      alt="WhatsApp"
                      className="w-5 h-5"
                    />
                    <span>Chat Now</span>
                  </a>
                </Button>
                
                <div className="flex items-center justify-center">
                  <Clock className="w-4 h-4 text-gray-500 mr-2" />
                  <p className="text-sm text-gray-500">Quick Response</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-start">
            <div>
              <SectionHeading
                title="Follow Us"
                gradient={true}
              />
              <div className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <div className="flex justify-center gap-6">
                  <a 
                    href="https://www.facebook.com/OMACUK/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-white p-4 rounded-full shadow-md hover:shadow-lg transition-shadow hover:bg-blue-100 transform hover:-translate-y-1 transition-transform duration-300"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-8 h-8 text-blue-600" />
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/omacuk2018/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-white p-4 rounded-full shadow-md hover:shadow-lg transition-shadow hover:bg-pink-100 transform hover:-translate-y-1 transition-transform duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-8 h-8 text-pink-600" />
                  </a>
                </div>
                <p className="text-center mt-4 text-gray-600">Stay connected with us on social media for updates, events, and inspiration!</p>
              </div>
            </div>

            <div className="space-y-6">
              <SectionHeading
                title="Find Us"
                gradient={true}
              />
              <div className="rounded-xl overflow-hidden shadow-lg h-[400px] bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2403.9144382121426!2d-1.0781342234275525!3d52.98148297199981!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4879c1ea74c2a4e9%3A0xcd06fe4a8d48ae9f!2s65%20Gedling%20Rd%2C%20Gedling%2C%20Nottingham%20NG4%203FG!5e0!3m2!1sen!2suk!4v1650900000000!5m2!1sen!2suk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="OMAC-UK Location Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Contact;
